import React, { useRef, useCallback, useReducer, useMemo } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { router } from '@/lib/navigation';
import { replaceAfterWebSignIn } from '@/lib/webSignInNavigation';
import { useTranslation } from '@/hooks/useTranslation';
import Text from '@/components/Typography/Text';
import FloatingLabelInput from '@/components/Inputs/FloatingLabelInput';
import { useAuthStore } from '@/store';
import Turnstile from '@/components/Turnstile';
import SubmitButton from '@/components/Buttons/SubmitButton';
import PropTypes from 'prop-types';
import Link from '@/components/Typography/Link';
import { authApi } from '@/lib/api';
import { getClientInfo, trackUser, trackWhopCompleteRegistration } from '@/constants/utils/analytics';
import { trackAuthEvent, AUTH_ACTION } from '@/constants/utils/analyticsEvents';
import useResponsiveness from '@/hooks/useResponsiveness';
import StatusContainer from '@/components/StatusContainer';
import { pendingReferral } from '@/lib/pendingReferral';

// Create styles outside component
const createStyles = (isXS) => StyleSheet.create({
  container: {
    width: '100%',
    maxWidth: isXS ? '100%' : 400,
    gap: 6,
  },
  inputContainer: {
    gap: 8,
  },
  footer: {
    gap: 12,
    marginTop: 24,
    alignItems: 'center',
  },
  footerLinks: {
    flexDirection: 'row',
    gap: 4,
    justifyContent: 'center',
  },
});

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Platform check
const isWeb = Platform.OS === 'web';

// Form validation
const validateEmail = (email, t) => {
  if (!email.trim()) {
    return t('auth.signup.errors.emailRequired');
  }
  if (!EMAIL_REGEX.test(email.trim())) {
    return t('auth.signup.errors.invalidEmail');
  }
  return null;
};

const validatePassword = (password, t) => {
  if (!password.trim()) {
    return t('auth.signup.errors.passwordRequired');
  }
  if (password.length < 8) {
    return t('auth.signup.errors.passwordTooShort');
  }
  return null;
};

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_EMAIL':
      return { ...state, email: action.payload, error: '' };
    case 'SET_PASSWORD':
      return { ...state, password: action.payload, error: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  email: '',
  password: '',
  error: '',
  isLoading: false,
};

// Memoized footer links component
const FormLinks = React.memo(({ styles, t, footerText, footerLinkText }) => (
  <View style={styles.footer}>
    <View style={styles.footerLinks}>
      <Text color='secondary'>{footerText}</Text>
      <Link href="/login">
        {footerLinkText}
      </Link>
    </View>
  </View>
));

FormLinks.propTypes = {
  styles: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  footerText: PropTypes.string.isRequired,
  footerLinkText: PropTypes.string.isRequired,
};

const GhostSignupForm = ({ 
  onSuccess, 
  showLinks = true,
  submitButtonText = 'Create Account',
  footerText = 'Already have an account?',
  footerLinkText = 'Log in'
}) => {
  const { t } = useTranslation();
  const setAuthData = useAuthStore(state => state.setAuthData);
  const turnstileRef = useRef(null);
  const { isXS } = useResponsiveness();
  const styles = useMemo(() => createStyles(isXS), [isXS]);
  
  const [formState, dispatch] = useReducer(formReducer, initialState);

  // Memoized handlers
  const handleEmailChange = useCallback((text) => {
    dispatch({ type: 'SET_EMAIL', payload: text });
  }, []);

  const handlePasswordChange = useCallback((text) => {
    dispatch({ type: 'SET_PASSWORD', payload: text });
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const emailError = validateEmail(formState.email, t);
    if (emailError) {
      dispatch({ type: 'SET_ERROR', payload: emailError });
      return false;
    }

    const passwordError = validatePassword(formState.password, t);
    if (passwordError) {
      dispatch({ type: 'SET_ERROR', payload: passwordError });
      return false;
    }

    return true;
  }, [formState.email, formState.password, t]);

  const handleSignup = useCallback(async () => {
    try {
      if (!validateForm()) return;

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: '' });

      // Get turnstile token on submit
      let turnstileToken = null;
      if (isWeb) {
        try {
          turnstileToken = await turnstileRef.current?.execute();
        } catch (captchaError) {
          dispatch({ type: 'SET_ERROR', payload: t('auth.signup.errors.captchaRequired') });
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
      }

      const referralCode = await pendingReferral.read();

      const data = await authApi.ghostSignup({
        email: formState.email,
        password: formState.password,
        ...(isWeb && { turnstileToken }),
        ...(referralCode && { referralCode }),
        clientInfo: getClientInfo(),
      });

      // Store auth data
      if (data.tokens?.access) {
        await setAuthData(data.tokens.access, data.tokens.expiresAt, data.tokens.refresh, data.user);
      }

      await pendingReferral.clear(referralCode);

      if (data.user?.id) trackUser(data.user.id);
      trackAuthEvent(AUTH_ACTION.SIGNUP_SUCCESS, 'ghost_upgrade');
      trackWhopCompleteRegistration();
      onSuccess?.(data);
      if (Platform.OS === 'web') replaceAfterWebSignIn(router, '/app');
      else router.replace('/(app)/app');
    } catch (error) {
      trackAuthEvent(AUTH_ACTION.SIGNUP_FAILED, 'ghost_upgrade');
      dispatch({ type: 'SET_ERROR', payload: error.message || t('auth.signup.errors.signupFailed') });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [formState.email, formState.password, t, onSuccess, setAuthData, validateForm]);

  return (
    <View style={styles.container}>
      <View style={styles.inputContainer}>
        <FloatingLabelInput
          label={t('auth.signup.emailLabel')}
          value={formState.email}
          onChangeText={handleEmailChange}
          keyboardType="email-address"
          autoCapitalize="none"
          onSubmitEditing={handleSignup}
          returnKeyType="next"
          autoComplete="email"
          textContentType="emailAddress"
        />

        <FloatingLabelInput
          label={t('auth.signup.passwordLabel')}
          value={formState.password}
          onChangeText={handlePasswordChange}
          secureTextEntry
          onSubmitEditing={handleSignup}
          returnKeyType="go"
          autoComplete="password"
          textContentType="password"
        />
      </View>

      {formState.error ? (
        <StatusContainer type="error" message={formState.error} />
      ) : null}

      {isWeb && <Turnstile ref={turnstileRef} />}

      <SubmitButton
        onPress={handleSignup}
        isLoading={formState.isLoading}
        isDisabled={!formState.email.trim() || !formState.password.trim()}
        title={submitButtonText}
      />

      {showLinks && (
        <FormLinks
          styles={styles}
          t={t}
          footerText={footerText}
          footerLinkText={footerLinkText}
        />
      )}
    </View>
  );
};

GhostSignupForm.propTypes = {
  onSuccess: PropTypes.func,
  showLinks: PropTypes.bool,
  submitButtonText: PropTypes.string,
  footerText: PropTypes.string,
  footerLinkText: PropTypes.string,
};

export default React.memo(GhostSignupForm);
